/**
 * Shared route guard for everything under src/app/admin/*. Mounted once by
 * app/admin/_layout.tsx so every admin screen (dashboard, approvals, users,
 * notices, polls, issues, vendors, create-*, users/[id]) is gated in one
 * place instead of relying on each screen (or the backend 403) to be the
 * only real defense — see audit 05-admin-crosscutting.md P0-3.
 */
import { usePathname } from 'expo-router';
import React from 'react';

import { goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useActingSociety } from '@/lib/acting-society';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { EmptyState, Screen, TopBar } from '@/ui';

/** Admin-only console areas a committee member may NOT enter (blocklist —
 * everything else under /admin is publishing they CAN use). Blocklist beats
 * allowlist here: a hard reload can render before usePathname settles, and an
 * allowlist would wrongly bounce committee off their own screens. The API is
 * the real guard — committee gets 403 on these endpoints regardless. */
const ADMIN_ONLY_PATHS = [
  '/admin/users', '/admin/approvals', '/admin/settings', '/admin/vendors',
  '/admin/amenities', '/admin/issues', '/admin/create-vendor', '/admin/create-amenity',
  '/admin/positions', '/admin/audit',
];
const ADMIN_ONLY_EXACT = ['/admin', '/admin/index'];

export function AdminGate({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  const { user, loading } = useSession();
  const pathname = usePathname();
  const acting = useActingSociety();
  // A platform staffer who has "entered" a society (see lib/acting-society.ts)
  // reuses this exact console — their session role stays PLATFORM_ADMIN (no
  // token swap), so admin-ness for them is conditional on an active acting
  // society, not their raw role alone. A PLATFORM_ADMIN NOT acting stays
  // blocked, same as any other non-admin visitor.
  const isAdmin = user?.role === 'SUB_ADMIN' || user?.role === 'MASTER_ADMIN'
    || (user?.role === 'PLATFORM_ADMIN' && !!acting);
  const onAdminOnly = ADMIN_ONLY_EXACT.includes(pathname)
    || ADMIN_ONLY_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const committeeAllowed = user?.role === 'COMMITTEE' && !onAdminOnly;

  // Session still resolving — keep the child route MOUNTED. On web hard
  // loads, rendering this layout without its <Slot/> makes expo-router
  // collapse the URL to /admin (replaceState), which is ADMIN_ONLY_EXACT and
  // then wrongly blocks committee deep links forever. Admin screens skeleton
  // while the session loads and their APIs 403 for non-admins, so nothing
  // leaks by mounting them for one resolution tick.
  if (loading) return <>{children}</>;

  if (!isAdmin && !committeeAllowed) {
    return (
      <Screen scroll={false}>
        <TopBar title="Admin" onBack={goBack} />
        <EmptyState
          icon={<Icon name="shield" size={40} color={colors.textTertiary} />}
          title="Admins only"
          body="This area is for society committee admins. Ask your committee if you think you should have access."
        />
      </Screen>
    );
  }

  return <>{children}</>;
}
